import { useCallback, useEffect, useMemo } from 'react';
import useLicenseList from '../../../license-management/hooks/useLicenseList';
import { useWizard } from '../../hooks/WizardProvider';
import { useBusinessTypeLookups } from '../../hooks/useBusinessTypeLookups';
import {
  organisationNeedsLicenseProfileResolve,
  patchOrganisationWithLicenseProfile,
} from '../../utils/licenseProfileResolver';
import {
  SUBDOMAIN_DISPLAY_POSTFIX,
  buildSubdomainPreviewUrl,
} from './organisationConstants';

function normalizeMultiSelect(value) {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

export default function useOrganisationStep() {
  const { draft, setDraft, steps, stepErrors, currentStep } = useWizard();
  const organisation = draft.organisation;
  const { businessTypes, loading: businessTypesLoading } = useBusinessTypeLookups();
  const { licenses, isLoading: licensesLoading, isUsingFallback, fetchLicenses } = useLicenseList();

  useEffect(() => {
    void fetchLicenses();
  }, [fetchLicenses]);

  useEffect(() => {
    if (!organisationNeedsLicenseProfileResolve(organisation) || licenses.length === 0) return;

    setDraft((prev) => {
      const patched = patchOrganisationWithLicenseProfile(prev.organisation, licenses);
      if (
        patched.license_permission_profile_id === prev.organisation.license_permission_profile_id &&
        patched.license_permission_profile_name === prev.organisation.license_permission_profile_name
      ) {
        return prev;
      }
      return { ...prev, organisation: patched };
    });
  }, [licenses, organisation.license_id, organisation.license_permission_profile_id, setDraft]);

  const subTypeOptions = useMemo(
    () => organisation.selectedBusinessTypes?.flatMap((item) => item?.subtypes || []) ?? [],
    [organisation.selectedBusinessTypes]
  );

  const previewUrl = useMemo(
    () => buildSubdomainPreviewUrl(organisation.subdomain),
    [organisation.subdomain]
  );

  const updateOrg = useCallback(
    (field, value) => {
      setDraft((prev) => ({
        ...prev,
        organisation: { ...prev.organisation, [field]: value },
      }));
    },
    [setDraft]
  );

  const onSelectLicense = useCallback(
    (licenseId) => {
      const selected = licenses.find((item) => String(item.id) === String(licenseId));
      const permissionProfileId =
        selected?.permissionProfileId ?? selected?.permissionProfile?.id ?? null;
      setDraft((prev) => ({
        ...prev,
        organisation: {
          ...prev.organisation,
          license_id: licenseId ? String(licenseId) : '',
          license_alias: selected?.alias ?? selected?.name ?? '',
          license_permission_profile_id:
            permissionProfileId != null ? String(permissionProfileId) : '',
          license_permission_profile_name: selected?.permissionProfile?.name ?? '',
        },
      }));
    },
    [licenses, setDraft]
  );

  const onSelectBusinessType = useCallback(
    (selectedBusinessTypes) => {
      const selected = normalizeMultiSelect(selectedBusinessTypes);
      setDraft((prev) => ({
        ...prev,
        organisation: {
          ...prev.organisation,
          selectedBusinessTypes: selected,
          business_type: selected.map((item) => item.id),
          selectedBusinessSubTypes: [],
          business_subtype: [],
        },
      }));
    },
    [setDraft]
  );

  const onSelectBusinessSubType = useCallback(
    (selectedBusinessSubTypes) => {
      const selected = normalizeMultiSelect(selectedBusinessSubTypes);
      setDraft((prev) => ({
        ...prev,
        organisation: {
          ...prev.organisation,
          selectedBusinessSubTypes: selected,
          business_subtype: selected.map((item) => item.id),
        },
      }));
    },
    [setDraft]
  );

  const handleLogo = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (file) updateOrg('logoFileName', file.name);
    },
    [updateOrg]
  );

  return {
    organisation,
    steps,
    stepErrors,
    currentStep,
    businessTypes,
    businessTypesLoading,
    licenses,
    licensesLoading,
    isUsingFallback,
    subTypeOptions,
    previewUrl,
    displayPostfix: SUBDOMAIN_DISPLAY_POSTFIX,
    updateOrg,
    onSelectLicense,
    onSelectBusinessType,
    onSelectBusinessSubType,
    handleLogo,
  };
}
